import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, AsyncBoundary, Notice, useToast } from '@kollez/design-system';
import { usePrepPlan, useRegeneratePrepPlan, useToggleTask, type PrepTaskRow, type PrepWeekRow } from '@/lib/queries';

/**
 * /student/prep (F72) — Nexie-generated week-by-week prep plan.
 *
 * Deep-audit fixes (Page 10/N):
 *   - Regenerate silently destroyed all task-completion progress. Now
 *     when a plan already exists, 'Refresh plan' shows a confirm-first
 *     warning so the student knows their ticks will reset.
 *   - No overall progress rollup — student saw per-week % but couldn't
 *     see 'X/Y done across the horizon'. Added a summary KPI strip.
 *   - Task 'Open' link used <Link to={href}> which React Router treats
 *     as an in-app path. External hrefs (Nexie can generate them) 404'd
 *     silently. Now uses an anchor for absolute URLs, Link otherwise.
 *   - Horizon input accepted values outside 1-12 via keyboard even
 *     though min/max were set (they're advisory in HTML5). Clamped in
 *     the change handler.
 */
export function StudentPrepPage() {
  const { data, isLoading, error, refetch } = usePrepPlan();
  const regen = useRegeneratePrepPlan();
  const toggle = useToggleTask();
  const toast = useToast();
  const [role, setRole] = useState('SDE-1');
  const [weeks, setWeeks] = useState(6);
  const [confirmingRegen, setConfirmingRegen] = useState(false);

  async function generate(force = false) {
    if (!role.trim()) { toast.show({ tone: 'warning', message: 'Pick a target role.' }); return; }
    // If a plan exists and the caller isn't confirmed, ask first.
    if (data?.plan && !force) { setConfirmingRegen(true); return; }
    try {
      await regen.mutateAsync({ targetRole: role.trim(), horizonWeeks: weeks });
      toast.show({ message: 'Nexie drafted your plan.' });
      setConfirmingRegen(false);
    } catch {
      toast.show({ tone: 'warning', message: 'Could not generate plan.' });
    }
  }

  const totals = data?.plan
    ? data.plan.weeks.reduce(
        (acc, w) => {
          const doneInWeek = w.tasks.filter((t) => t.done).length;
          return { done: acc.done + doneInWeek, total: acc.total + w.tasks.length };
        },
        { done: 0, total: 0 },
      )
    : null;
  const overallPct = totals && totals.total ? Math.round((totals.done / totals.total) * 100) : 0;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Prep plan</p>
          <h1 className="h-page">Your personalized plan</h1>
          <p className="subhead">Nexie reads your readiness score, your gaps, and your upcoming interviews — and lays out the next {weeks} weeks.</p>
        </div>
      </div>

      <Card padding="lg">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Target role</span>
            <input value={role} onChange={(e) => setRole(e.target.value)} className="input" placeholder="SDE-1, Data Analyst…" />
          </label>
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Horizon (weeks)</span>
            <input
              type="number" min={1} max={12} value={weeks}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (!Number.isFinite(n)) return;
                setWeeks(Math.max(1, Math.min(12, Math.round(n))));
              }}
              className="input"
            />
          </label>
          <div className="flex items-end">
            <Button variant="primary" onClick={() => generate(false)} loading={regen.isPending}>
              {data?.plan ? 'Refresh plan' : 'Generate plan'}
            </Button>
          </div>
        </div>
        {confirmingRegen && (
          <div className="mt-3">
            <Notice tone="warning">
              Regenerating replaces every task and clears your ticks so far.
              <div className="flex gap-2 mt-2">
                <Button variant="primary" onClick={() => generate(true)} loading={regen.isPending}>Yes, regenerate</Button>
                <Button variant="secondary" onClick={() => setConfirmingRegen(false)}>Cancel</Button>
              </div>
            </Notice>
          </div>
        )}
      </Card>

      <AsyncBoundary kind="cards" loading={isLoading} error={error} onRetry={() => void refetch()}>
        {data?.plan ? (
          <>
            <Card padding="lg">
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <div>
                  <h2 className="h-section">{data.plan.targetRole} · {data.plan.horizonWeeks} weeks</h2>
                  <div className="text-xs text-ink-tertiary">Generated {new Date(data.plan.generatedAt).toLocaleDateString('en-IN')}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone="info">Readiness {data.plan.contextSnapshot.profile360Score}</Badge>
                  {typeof data.plan.contextSnapshot.recentMockScore === 'number' && (
                    <Badge tone="outline">Last mock {data.plan.contextSnapshot.recentMockScore}</Badge>
                  )}
                  {totals && (
                    <Badge tone={overallPct === 100 ? 'success' : overallPct > 0 ? 'info' : 'outline'}>
                      {totals.done}/{totals.total} tasks · {overallPct}%
                    </Badge>
                  )}
                </div>
              </div>
              <p className="text-ink-secondary leading-relaxed">{data.plan.summary}</p>
            </Card>

            <div className="grid gap-3">
              {data.plan.weeks.map((w) => <WeekCard key={w.weekIndex} week={w} onToggle={(t, done) => toggle.mutate({ weekIndex: w.weekIndex, taskId: t._id, done })} />)}
            </div>
          </>
        ) : (
          <Card padding="lg">
            <Notice tone="info">No plan yet — set a target role above and hit Generate. Nexie will read your profile and lay out the next few weeks.</Notice>
          </Card>
        )}
      </AsyncBoundary>
    </div>
  );
}

function WeekCard({ week, onToggle }: { week: PrepWeekRow; onToggle: (task: PrepTaskRow, done: boolean) => void }) {
  const done = week.tasks.filter((t) => t.done).length;
  const total = week.tasks.length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Card padding="md">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-tertiary">Week {week.weekIndex}</div>
          <div className="font-medium">{week.focus}</div>
        </div>
        <Badge tone={pct === 100 ? 'success' : pct > 0 ? 'info' : 'outline'}>{done}/{total}</Badge>
      </div>
      {week.rationale && <div className="text-sm text-ink-tertiary mb-2">{week.rationale}</div>}
      <ul className="grid gap-1 text-sm">
        {week.tasks.map((t) => (
          <li key={t._id} className="flex items-start gap-2">
            <input type="checkbox" checked={t.done} onChange={(e) => onToggle(t, e.target.checked)} className="mt-1" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className={t.done ? 'line-through text-ink-tertiary' : ''}>{t.label}</span>
                <Badge tone="outline">{t.kind}</Badge>
                {typeof t.estMinutes === 'number' && <span className="text-xs text-ink-tertiary">~{t.estMinutes}m</span>}
              </div>
              {t.href && (/^https?:\/\//i.test(t.href)
                ? <a href={t.href} target="_blank" rel="noopener noreferrer" className="text-ink-info text-xs underline">Open →</a>
                : <Link to={t.href} className="text-ink-info text-xs underline">Open →</Link>)}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
